import type { BoardCard } from '@nightshift/ui-types';

/**
 * A card that is waiting its turn rather than waiting for a person.
 *
 * Both halves matter. `dependency` is the reason a queue exists at all, and `self-waking` is
 * the promise the colour makes: this one starts on its own. A dependency park that has been
 * handed back to a person -- every blocker left was cancelled, so it can never clear itself --
 * keeps the attention colour, which is the whole point of handing it back.
 */
export function isQueuedCard(
  card: Pick<BoardCard, 'state' | 'waitingReason' | 'waitingSelfWaking'>,
): boolean {
  return card.state === 'Waiting' && card.waitingReason === 'dependency' && card.waitingSelfWaking;
}

export function activeElapsed(since: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * How loudly the Done card should wear its shipping state. `good` stays for
 * callers that only need shipped-or-not; `tone` is what the chip colours by,
 * because a running deploy and an unverified one are not the same news.
 */
type ShippingTone = 'shipped' | 'deployed' | 'running' | 'failed' | 'unknown';

export interface ShippingStatus {
  label: string;
  detail: string;
  good: boolean;
  tone: ShippingTone;
}

function status(label: string, detail: string, tone: ShippingTone): ShippingStatus {
  return { label, detail, good: tone === 'shipped', tone };
}

function sameRevision(left: string | null | undefined, right: string | null | undefined): boolean {
  if (!left || !right) return false;
  const a = left.toLowerCase();
  const b = right.toLowerCase();
  return a.startsWith(b) || b.startsWith(a);
}

export function shippingLabel(card: BoardCard, now: number): ShippingStatus | null {
  if (card.state !== 'Done') return null;
  const shipping = card.shipping;
  if (shipping && now - shipping.observedAt <= 180_000 && shipping.status !== 'unknown') {
    const detail = `${shipping.environment}: ${shipping.detail}. Revision ${shipping.sha ?? 'unknown'}.`;
    return shipping.status === 'shipped'
      ? status(`Shipped${shipping.version ? ` · ${shipping.version.split('+')[0]}` : ''}`, detail, 'shipped')
      : status('Not yet shipped', detail, 'unknown');
  }
  const deploy = card.latestDeploy;
  if (deploy && (deploy.status === 'queued' || deploy.status === 'running')) {
    return status(
      `Deploying · ${deploy.status}`,
      `${deploy.environment}: ${deploy.jobName} is ${deploy.status} for revision ${deploy.sha.slice(0, 12)}.`,
      'running',
    );
  }
  if (deploy && (deploy.status === 'failed' || deploy.status === 'canceled')) {
    return status(
      `Deploy ${deploy.status}`,
      deploy.healthDetail ?? `${deploy.environment}: ${deploy.jobName} ${deploy.status} for revision ${deploy.sha.slice(0, 12)}.`,
      'failed',
    );
  }
  if (deploy?.status === 'succeeded' && deploy.healthStatus === 'failed') {
    return status(
      'Health check failed',
      deploy.healthDetail ?? `${deploy.environment}: the deploy job succeeded but the health check did not pass.`,
      'failed',
    );
  }
  if (deploy?.status === 'succeeded' && (deploy.healthStatus === 'passed' || deploy.healthStatus === 'skipped')) {
    const release = deploy.releaseVersion
      ?? (shipping?.version && sameRevision(shipping.sha, deploy.sha) ? shipping.version : null);
    const version = release?.split('+')[0];
    const health = deploy.healthStatus === 'passed'
      ? 'The deployment health check passed.'
      : 'No deployment health check was configured.';
    return status(
      `Deployed${version ? ` · ${version}` : ''}`,
      `${deploy.environment}: deployed revision ${deploy.sha.slice(0, 12)}${release ? ` as release ${release}` : ''} at ${new Date(deploy.finishedAt ?? deploy.updatedAt).toLocaleString()}. ${health}`,
      'deployed',
    );
  }
  return status(
    'Shipping unverified',
    shipping && now - shipping.observedAt > 180_000
      ? `Deployment observation is overdue. Last observation: ${shipping.detail}`
      : (shipping?.detail ?? 'Done means completed. No deployed revision has been verified for this ticket.'),
    'unknown',
  );
}
